import { Components, Theme } from '@mui/material';
import { dateRangePickerDayClasses } from '@mui/x-date-pickers-pro';
import { GetThemeOptions } from '../../../theme/theme';

/**
 * Component overrides (i.e. global styles, default props) applied to the MUI
 * MuiDateRangePickerDay to make it match the design system.
 *
 * See: https://mui.com/x/api/date-pickers/date-range-picker-day/#css
 */
const borderRadius = '4px';

const endBorderStyle = {
  borderTopRightRadius: borderRadius,
  borderBottomRightRadius: borderRadius,
};
const startBorderStyle = {
  borderTopLeftRadius: borderRadius,
  borderBottomLeftRadius: borderRadius,
};

export const getMuiDateRangePickerDay = (opts: GetThemeOptions): Components<Theme>['MuiDateRangePickerDay'] => ({
  styleOverrides: {
    root: {
      borderRadius: 0,
      '&:first-of-type': {
        // highlight start of week
        ...startBorderStyle,

        // preview start of week
        [`.${dateRangePickerDayClasses.rangeIntervalDayPreview}`]: {
          ...startBorderStyle,
        },
      },
      '&:last-of-type': {
        // highlight end of week
        ...endBorderStyle,

        // preview end of week
        [`.${dateRangePickerDayClasses.rangeIntervalDayPreview}`]: {
          ...endBorderStyle,
        },
      },
      [`&.${dateRangePickerDayClasses.firstVisibleCell}`]: {
        ...startBorderStyle,
      },
      [`&.${dateRangePickerDayClasses.lastVisibleCell}`]: {
        ...endBorderStyle,
      },
    },

    day: ({ theme }) => {
      const selectedColor = opts.panwRebrandStubYear
        ? theme.palette.button.standard.primary.default
        : theme.palette.primary.main;

      return {
        borderRadius: 0,
        width: '36px',
        height: '24px',
        // today
        '&:not(.Mui-selected)': {
          borderRadius,
          borderColor: selectedColor,
          // hover for non-active elements
          [`&.${dateRangePickerDayClasses.dayOutsideRangeInterval}:hover`]: {
            borderColor: 'transparent',
            backgroundColor: theme.palette.divider,
          },
        },
        // start-only range selection
        [`&.Mui-selected.${dateRangePickerDayClasses.dayOutsideRangeInterval}`]: {
          borderRadius,
          background: selectedColor,
        },
      };
    },

    outsideCurrentMonth: {
      [`.${dateRangePickerDayClasses.rangeIntervalPreview}`]: {
        background: 'transparent',
      },
    },

    rangeIntervalDayHighlight: ({ theme }) => {
      if (!opts.panwRebrandStubYear) return {};
      return {
        backgroundColor: theme.palette.background.hover,
      };
    },

    rangeIntervalDayPreviewStart: {
      ...startBorderStyle,
    },
    rangeIntervalDayPreviewEnd: {
      ...endBorderStyle,
    },

    rangeIntervalDayHighlightStart: ({ theme }) => {
      const selectedColor = opts.panwRebrandStubYear
        ? theme.palette.button.standard.primary.default
        : theme.palette.primary.main;
      return {
        [`.${dateRangePickerDayClasses.rangeIntervalPreview}`]: {
          background: selectedColor,
          ...startBorderStyle,
        },
        [`.${dateRangePickerDayClasses.day}`]: {
          ...startBorderStyle,
        },
      };
    },
    rangeIntervalDayHighlightEnd: ({ theme }) => {
      const selectedColor = opts.panwRebrandStubYear
        ? theme.palette.button.standard.primary.default
        : theme.palette.primary.main;
      return {
        [`.${dateRangePickerDayClasses.rangeIntervalPreview}`]: {
          background: selectedColor,
          ...endBorderStyle,
        },
        [`.${dateRangePickerDayClasses.day}`]: {
          ...endBorderStyle,
        },
      };
    },
  },
});
